import { useRequiredContext } from "@redotech/react-util/context";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { REDO_EFFECT_API_URL } from "@redotech/redo-merchant-app-common/config";
import {
  EffectApiClientContext,
  EffectApiClientProvider,
  type EffectApiContext,
} from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { merchantQueryClient } from "@redotech/redo-merchant-app-common/react-query-client";
import { useSupportTicketQueryScope } from "@redotech/redo-merchant-app-common/support/use-support-ticket-query-scope";
import type { SupportTicketQueryScope } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
import { memo, type ReactNode, useContext, useMemo, useRef } from "react";
import { KnowledgeCaptureProvider } from "../../context/knowledge-capture-context";
import { KnowledgeCaptureRenderer } from "../../knowledge-capture/knowledge-capture-renderer";
import { createGlobalSupportTicketCollections } from "../db/collections";
import { NotificationSyncHandlers } from "../stream/notification-sync-handlers";
import { GlobalSupportTicketCollectionsContext } from "./global-support-ticket-collections-context";

const GlobalSupportTicketStoreProvider = memo(
  function GlobalSupportTicketStoreProvider({
    children,
    queryScope,
    syncNotifications,
  }: {
    children: ReactNode;
    queryScope: SupportTicketQueryScope;
    /** Mount the stream sync handlers; only the helpdesk routes need them. */
    syncNotifications: boolean;
  }) {
    const effectApiClient = useRequiredContext(EffectApiClientContext);

    // Keep a ref to the latest effectApi so the stable proxy always calls
    // through to the current (non-disposed) runtime.
    const effectApiRef = useRef(effectApiClient);
    effectApiRef.current = effectApiClient;

    const stableEffectApi = useMemo<EffectApiContext>(
      () => ({
        runPromise: (...args) => effectApiRef.current.runPromise(...args),
        getClient: () => effectApiRef.current.getClient(),
      }),
      // Stable proxy — intentionally created once, reads from ref at call time.

      [],
    );

    const collections = useMemo(() => {
      return createGlobalSupportTicketCollections({
        queryClient: merchantQueryClient,
        effectApi: stableEffectApi,
        queryScope,
        tagQueryScope: queryScope,
      });
    }, [stableEffectApi, queryScope]);

    return (
      <GlobalSupportTicketCollectionsContext.Provider value={collections}>
        <KnowledgeCaptureProvider>
          {syncNotifications && <NotificationSyncHandlers />}
          <KnowledgeCaptureRenderer />
          {children}
        </KnowledgeCaptureProvider>
      </GlobalSupportTicketCollectionsContext.Provider>
    );
  },
);

/** Provides collections to support-v2 UI outside the helpdesk routes. */
export const SupportTicketCollectionsBoundary = memo(
  function SupportTicketCollectionsBoundary({
    children,
  }: {
    children: ReactNode;
  }) {
    const queryScope = useSupportTicketQueryScope();
    const existingCollections = useContext(
      GlobalSupportTicketCollectionsContext,
    );

    if (existingCollections) {
      return <>{children}</>;
    }

    return (
      <GlobalSupportTicketStoreProvider
        queryScope={queryScope}
        syncNotifications={false}
      >
        {children}
      </GlobalSupportTicketStoreProvider>
    );
  },
);

interface SupportProvidersProps {
  children: ReactNode;
}

export const SupportProviders = memo(function SupportProviders({
  children,
}: SupportProvidersProps) {
  const auth = useContext(AuthContext);
  const queryScope = useSupportTicketQueryScope();

  return (
    <EffectApiClientProvider
      authToken={auth?.token}
      baseUrl={REDO_EFFECT_API_URL}
    >
      <GlobalSupportTicketStoreProvider
        queryScope={queryScope}
        syncNotifications
      >
        {children}
      </GlobalSupportTicketStoreProvider>
    </EffectApiClientProvider>
  );
});
